import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import ModalShell from "@/components/ModalShell";

const APOLLO_COLORS = [
  { hex: "#E15C2C", name: "Orange" },
  { hex: "#FBC90C", name: "Yellow" },
  { hex: "#B02065", name: "Purple" },
  { hex: "#050505", name: "Black" },
  { hex: "#FFFFFF", name: "White" },
];

export default function CampaignModal({ open, onClose, campaign, onSaved }) {
  const isEdit = Boolean(campaign);
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    description: "",
    hero_image: "",
    color: "#FBC90C",
    launch_date: new Date().toISOString().slice(0, 10),
    status: "active",
    featured: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (campaign) {
      setForm({
        name: campaign.name || "",
        tagline: campaign.tagline || "",
        description: campaign.description || "",
        hero_image: campaign.hero_image || "",
        color: campaign.color || "#FBC90C",
        launch_date: campaign.launch_date || new Date().toISOString().slice(0, 10),
        status: campaign.status || "active",
        featured: !!campaign.featured,
      });
    } else if (open) {
      setForm({
        name: "",
        tagline: "",
        description: "",
        hero_image: "",
        color: "#FBC90C",
        launch_date: new Date().toISOString().slice(0, 10),
        status: "active",
        featured: true,
      });
    }
  }, [campaign, open]);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/admin/campaigns/${campaign.id}`, form);
        toast.success("Campaign updated");
        onSaved && onSaved({ updated: data });
      } else {
        const { data } = await api.post("/admin/campaigns", form);
        toast.success("Campaign created");
        onSaved && onSaved({ created: data });
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  }

  return (
    <ModalShell open={open} onClose={onClose} maxWidth="max-w-xl" testId="campaign-modal">
      <button onClick={onClose} className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm" data-testid="campaign-close" aria-label="Close">
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">{isEdit ? "Edit Campaign" : "New Campaign"}</div>
      <h2 className="font-display text-3xl uppercase font-bold tracking-tight">{isEdit ? campaign.name : "Create Campaign"}</h2>

      <form onSubmit={submit} className="mt-6 space-y-5">
          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="camp-name" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
          </Field>
          <Field label="Tagline">
            <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="camp-tagline" placeholder="Short, punchy line" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
          </Field>
          <Field label="Description">
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="camp-desc" className="w-full bg-transparent border border-border rounded-sm p-3 text-sm outline-none focus:border-foreground" />
          </Field>
          <Field label="Hero Image URL">
            <input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} data-testid="camp-hero" placeholder="https://…" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
          </Field>

          {form.hero_image && (
            <div className="aspect-[16/7] rounded-sm border border-border overflow-hidden bg-muted">
              <img src={form.hero_image} alt="Preview" className="w-full h-full object-cover" />
            </div>
          )}

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 block">Accent Color</label>
            <div className="flex gap-2">
              {APOLLO_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setForm({ ...form, color: c.hex })}
                  title={c.name}
                  data-testid={`camp-color-${c.name.toLowerCase()}`}
                  className={`w-8 h-8 rounded-sm border-2 transition ${form.color === c.hex ? "border-foreground" : "border-transparent"}`}
                  style={{ background: c.hex }}
                />
              ))}
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-8 h-8 rounded-sm border-2 border-transparent p-0 bg-transparent cursor-pointer"
                data-testid="camp-color-custom"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Launch Date">
              <input type="date" value={form.launch_date} onChange={(e) => setForm({ ...form, launch_date: e.target.value })} data-testid="camp-date" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="camp-status" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground capitalize">
                <option value="active">Active</option>
                <option value="upcoming">Upcoming</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="camp-featured" className="accent-foreground" />
            Feature on dashboard
          </label>

          <button type="submit" disabled={busy} className="w-full bg-foreground text-background py-3 uppercase tracking-widest text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="camp-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save Changes" : "Create Campaign"}
          </button>
      </form>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">{label}</label>
      {children}
    </div>
  );
}
